/**
 * ContractorPendingOffersPanel
 *
 * Surfaces ACTIONABLE direct pending offers at the top of the Opportunities
 * tab — the canonical destination after a contractor clicks a pending-offer
 * item from Home / agenda / bridge.
 *
 * Notes:
 *   • Reads pending offers via canonical `filterContractorPendingOffers`.
 *   • Renders for BOTH offer_only and limited_exposure modes — pending offers
 *     are independent of marketplace visibility and must always be reachable.
 *   • Accept/Decline route through `useMarketplaceOffers` (toasts handled there).
 *   • Observer mode is honored by the parent <fieldset disabled> in ContractorPortal.
 *   • Pricing surface: payoutAmountSnapshot only — never customer revenue.
 *   • Optional `focusJobId` scroll-into-view + ring highlight when navigating
 *     from Home with ?jobId=.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Inbox, MapPin, Calendar, DollarSign, Clock } from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentContractor } from "@/hooks/useCurrentContractor";
import { useMarketplaceOffers } from "@/hooks/useMarketplaceOffers";
import { filterContractorPendingOffers } from "@/data/contractor/commandCenter";
import {
  OFFER_DECLINE_REASONS,
  type OfferDeclineReason,
} from "@/data/contractor/marketplaceOffer";

function formatTimeLeft(ms: number): string {
  if (ms <= 0) return "expired";
  const hrs = Math.floor(ms / (1000 * 60 * 60));
  if (hrs >= 24) {
    const days = Math.floor(hrs / 24);
    return `${days}d left`;
  }
  if (hrs >= 1) return `${hrs}h left`;
  const mins = Math.max(1, Math.floor(ms / (1000 * 60)));
  return `${mins}m left`;
}

interface Props {
  focusJobId?: string | null;
  /** Open the Phase A review sheet for this offer. */
  onOpenReview?: (offerId: string) => void;
}

export function ContractorPendingOffersPanel({ focusJobId, onOpenReview }: Props) {
  const { jobs, marketplaceOffers } = useAppData();
  const { id: contractorId } = useCurrentContractor();
  const { accept, decline } = useMarketplaceOffers();

  const pending = useMemo(
    () => filterContractorPendingOffers(marketplaceOffers, contractorId),
    [marketplaceOffers, contractorId],
  );

  const jobLookup = useMemo(() => {
    const m = new Map<string, (typeof jobs)[number]>();
    for (const j of jobs) m.set(j.id, j);
    return m;
  }, [jobs]);

  const [declineReason, setDeclineReason] = useState<
    Record<string, OfferDeclineReason | undefined>
  >({});
  const [openDecline, setOpenDecline] = useState<Record<string, boolean>>({});

  const focusRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (focusJobId && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusJobId, pending.length]);

  if (pending.length === 0) return null;

  return (
    <Card data-testid="contractor-pending-offers-panel">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-2">
            <Inbox className="h-4 w-4" /> Pending Offers
          </h2>
          <Badge variant="outline" className="text-[10px]">
            {pending.length} awaiting response
          </Badge>
        </div>

        <ul className="space-y-2">
          {pending.map((o) => {
            const job = jobLookup.get(o.jobId);
            const isFocus = focusJobId === o.jobId;
            const expiresIn =
              new Date(o.expiresAt).getTime() - Date.now();
            const declineOpen = !!openDecline[o.id];
            const reason = declineReason[o.id];
            return (
              <li
                key={o.id}
                ref={isFocus ? focusRef : undefined}
                data-testid="pending-offer-card"
                data-job-id={o.jobId}
                className={`rounded-lg border p-3 bg-muted/10 transition-shadow ${
                  isFocus
                    ? "border-primary/40 ring-1 ring-primary/30 shadow-sm"
                    : "border-border/60"
                }`}
              >
                <button
                  type="button"
                  onClick={
                    onOpenReview ? () => onOpenReview(o.id) : undefined
                  }
                  className={`w-full text-left flex items-start justify-between gap-2 ${
                    onOpenReview
                      ? "cursor-pointer hover:opacity-90 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/30 rounded"
                      : ""
                  }`}
                  data-testid="pending-offer-open-review"
                  disabled={!onOpenReview}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold truncate">
                      {job?.customerName ?? o.jobId}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {job?.projectType ?? "—"}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground mt-1">
                      {job?.address?.city && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {job.address.city}
                          {job.address.zip ? ` · ${job.address.zip}` : ""}
                        </span>
                      )}
                      {job?.scheduledInstallDate && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {job.scheduledInstallDate}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatTimeLeft(expiresIn)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs font-semibold inline-flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      {(o.payoutAmountSnapshot ?? 0).toLocaleString()}
                    </div>
                    <p className="text-[10px] text-muted-foreground">payout</p>
                  </div>
                </button>

                {!declineOpen ? (
                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      size="sm"
                      className="h-7 text-[11px]"
                      onClick={() => accept(o.id, contractorId)}
                      data-testid="pending-offer-accept"
                    >
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px]"
                      onClick={() =>
                        setOpenDecline((p) => ({ ...p, [o.id]: true }))
                      }
                      data-testid="pending-offer-decline-open"
                    >
                      Decline
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-3">
                    <Select
                      value={reason}
                      onValueChange={(v) =>
                        setDeclineReason((p) => ({
                          ...p,
                          [o.id]: v as OfferDeclineReason,
                        }))
                      }
                    >
                      <SelectTrigger className="h-7 text-[11px] flex-1">
                        <SelectValue placeholder="Reason for decline" />
                      </SelectTrigger>
                      <SelectContent>
                        {OFFER_DECLINE_REASONS.map((r) => (
                          <SelectItem
                            key={r.value}
                            value={r.value}
                            className="text-[11px]"
                          >
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-7 text-[11px]"
                      disabled={!reason}
                      onClick={() => {
                        if (!reason) return;
                        decline(o.id, contractorId, reason);
                        setOpenDecline((p) => ({ ...p, [o.id]: false }));
                      }}
                      data-testid="pending-offer-decline-confirm"
                    >
                      Confirm
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-[11px]"
                      onClick={() =>
                        setOpenDecline((p) => ({ ...p, [o.id]: false }))
                      }
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

export default ContractorPendingOffersPanel;
